'use client';

import { useState, useEffect } from 'react';
import { useNavBehavior } from '@/hooks/useNavBehaviour';
import type { NavItem } from '@/lib/contentful/api/getWebsiteConfig';
import DesktopNav from './DesktopNav';
import HamburgerButton from './HamburgerButton';
import MobileDrawer from './MobileDrawer';

type CtaButton = { text: string; url: string; isNewTab: boolean } | null;

type Props = {
  navItems: NavItem[];
  ctaButton: CtaButton;
};

export default function NavClient({ navItems, ctaButton }: Props) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    handler();
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const { isActive, handleClick } = useNavBehavior(navItems);

  return (
    <>
      <style>{`
        #main-nav {
          padding: ${scrolled ? '0.85rem 6vw' : '1.6rem 6vw'};
          background: var(--color-bg-alt);
          border-bottom-color: ${scrolled ? 'var(--color-border)' : 'transparent'};
          box-shadow: ${scrolled ? '0 2px 14px rgba(44,32,24,0.1)' : 'none'};
        }
      `}</style>

      <DesktopNav
        navItems={navItems}
        ctaButton={ctaButton}
        scrolled={scrolled}
        isActive={isActive}
        handleClick={handleClick}
      />

      <HamburgerButton open={open} onClick={() => setOpen((v) => !v)} />

      <MobileDrawer
        open={open}
        navItems={navItems}
        ctaButton={ctaButton}
        isActive={isActive}
        handleClick={handleClick}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
